import { Component, OnInit, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../services/logger.service';
import { MenuNavigationService } from '../../../../core/services/menu-navigation.service';
import { Router } from '@angular/router';
import { NavbarComponent } from '../../../../shared/layout/navbar/navbar.component';
import { SidebarComponent } from '../../../../shared/layout/sidebar/sidebar.component';
import { PageHeaderComponent } from '../../../../shared/layout/page-header/page-header.component';
import { ChatComponent } from '../../../shared/components/chat/chat.component';
import { AuthService, AuthUser } from '../../../../core/services/auth.service';
import { LayoutService } from '../../../../services/layout.service';
import { TranslationService } from '../../../../services/translation.service';
import { ApiService } from '../../../../core/services/api.service';
import { firstValueFrom } from 'rxjs';

interface ActivityLog {
  id: string;
  actor_id: string;
  actor_type: string;
  actor_name: string;
  action: string;
  entity_type: string;
  entity_id: string;
  description: string;
  hospital_id: string;
  patient_id: string;
  created_at: string;
}

interface ActivityLogsResponse {
  items: ActivityLog[];
  total: number;
}

interface RecentActivity {
  id: string;
  name: string;
  activity: string;
  date: string;
}

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, NavbarComponent, SidebarComponent, PageHeaderComponent, ChatComponent],
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
})
export class AdminDashboardComponent implements OnInit {
  currentUser = signal<AuthUser | null>(null);
  selectedMenu = signal('dashboard');

  // Chat state
  isChatOpen = signal(false);

  // Dashboard stats
  totalAppointments = signal(0);
  totalPatients = signal(0);
  activeMedics = signal(0);

  // Chart data for patient trends
  patientData = [
    { day: 'Mon', value: 285 },
    { day: 'Tue', value: 320 },
    { day: 'Wed', value: 250 },
    { day: 'Thu', value: 380 },
    { day: 'Fri', value: 420 },
    { day: 'Sat', value: 390 },
    { day: 'Sun', value: 350 },
  ];

  // Traffic by location data
  trafficData = [
    { location: 'In-patients', percentage: 63, color: '#3B82F6' },
    { location: 'Out-patients', percentage: 24, color: '#64748B' },
    { location: 'Other', percentage: 13, color: '#94A3B8' },
  ];

  // Recent activities
  recentActivities = signal<RecentActivity[]>([]);

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly logger: LoggerService,
    private readonly layoutService: LayoutService,
    private readonly menuNavigationService: MenuNavigationService,
    private readonly apiService: ApiService,
    public readonly translationService: TranslationService
  ) {
    // Use effect to monitor authentication changes
    effect(() => {
      const user = this.authService.currentUser();
      if (user) {
        this.currentUser.set(user);
      }
    });
  }

  ngOnInit() {
    const user = this.authService.currentUser();
    if (user) {
      this.logger.debug('AdminDashboardComponent - User found:', user);
      this.currentUser.set(user);
    }

    // Load dashboard data
    this.loadTotalAppointments();
    this.loadTotalPatients();
    this.loadRecentActivities();
  }

  getUserRole(): string {
    return this.authService.getSimplifiedRole() || 'admin';
  }

  async loadTotalAppointments() {
    const user = this.currentUser();
    if (!user?.hospital_id) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.authGet<{ total: number }>(
          `secure/hospitals/${user.hospital_id}/appointments/count`
        )
      );
      this.totalAppointments.set(response.total);
    } catch (error) {
      this.logger.error('Error loading total appointments:', error);
      this.totalAppointments.set(0);
    }
  }

  async loadTotalPatients() {
    const user = this.currentUser();
    if (!user?.hospital_id) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.authGet<{ hospital_id: string; patient_count: number }>(
          `secure/hospitals/${user.hospital_id}/patients/count`
        )
      );
      this.totalPatients.set(response.patient_count);
    } catch (error) {
      this.logger.error('Error loading total patients:', error);
      this.totalPatients.set(0);
    }
  }

  async loadRecentActivities() {
    const user = this.currentUser();
    if (!user?.hospital_employee_id) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.authGet<ActivityLogsResponse>(
          `secure/activity-logs/actor/${user.hospital_employee_id}?page=1&per_page=10`
        )
      );

      const activities: RecentActivity[] = response.items.map((log, index) => ({
        id: (index + 1).toString().padStart(2, '0'),
        name: log.actor_name,
        activity: log.description,
        date: new Date(log.created_at).toISOString().split('T')[0],
      }));

      this.recentActivities.set(activities);
    } catch (error) {
      this.logger.error('Error loading recent activities:', error);
      this.recentActivities.set([]);
    }
  }

  isSidebarCollapsed() {
    return this.layoutService.isSidebarCollapsed();
  }

  onMenuSelect(menu: string) {
    this.selectedMenu.set(menu);

    this.menuNavigationService.navigateToMenu(menu, {
      currentPage: 'dashboard',
    });
  }

  onFilterClicked() {}

  // Chat methods
  openChat(): void {
    this.isChatOpen.set(true);
  }

  closeChat(): void {
    this.isChatOpen.set(false);
  }

  // Get chart max value for scaling
  getChartMaxValue(): number {
    return Math.max(...this.patientData.map((d) => d.value)) + 50;
  }

  // Get chart point coordinates
  getChartPath(): string {
    const width = 400;
    const height = 100;
    const maxValue = this.getChartMaxValue();

    const points = this.patientData.map((point, index) => {
      const x = (index / (this.patientData.length - 1)) * width;
      const y = height - (point.value / maxValue) * height;
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  }
}
